import * as Auth from 'aws-amplify/auth';
import { AuthError, FetchUserAttributesOutput } from 'aws-amplify/auth';
import { useCallback, useState } from 'react';
import { appConfig } from 'shared/appConfig';
import { useCreateExpertMutation } from 'shared/hooks/api/createExpert/createExpert.generated';
import { useExpertDataLazyQuery } from 'shared/hooks/api/expertData/expertData.generated';
import { useResendOtpMutation } from 'shared/hooks/api/resendOtp/resendOtp.generated';
import { useStartPasswordResetMutation } from 'shared/hooks/api/startPasswordReset/startPasswordReset.generated';
import { useVerifyPasswordResetOtpMutation } from 'shared/hooks/api/verifyPasswordResetOtp/verifyPasswordResetOtp.generated';
import { useAuthStore } from 'shared/hooks/stores/useAuthStore';
import { useExpertProfileStore } from 'shared/hooks/stores/useExpertProfileStore';
import { useOnboardingStore } from 'shared/hooks/stores/useOnboardingStore';

export enum CognitoErrorCodesEnum {
  NotAuthorizedException = 'NotAuthorizedException',
  UserNotFoundException = 'UserNotFoundException',
  UserNotConfirmedException = 'UserNotConfirmedException',
  PasswordResetRequiredException = 'PasswordResetRequiredException',
  LimitExceededException = 'LimitExceededException',
  InvalidParameterException = 'InvalidParameterException',
  UnexpectedLambdaException = 'UnexpectedLambdaException',
  UserLambdaValidationException = 'UserLambdaValidationException',
  TooManyRequestsException = 'TooManyRequestsException',
  ExpiredCodeException = 'ExpiredCodeException',
  UserAlreadyAuthenticatedException = 'UserAlreadyAuthenticatedException',
}

const handleCognitoError = (err: unknown): never => {
  console.error('Cognito Error:', err);
  if (typeof err === 'object' && err !== null && 'name' in err) {
    const errorName = (err as any).name;

    if (Object.values(CognitoErrorCodesEnum).includes(errorName)) {
      throw new Error(errorName);
    }

    throw new Error('Unknown Cognito Error');
  }

  throw new Error('UnknownError');
};

export const getAuthorizationToken = async () => {
  try {
    return (await Auth.fetchAuthSession()).tokens?.idToken?.toString();
  } catch (err) {
    handleCognitoError(err);
    return null;
  }
};

const prepareEmail = (email: string) => email.trim().toLowerCase();

export enum CognitoAuthFlowEnum {
  EMAIL_OTP = 'EMAIL_OTP',
  USER_PASSWORD = 'USER_PASSWORD',
  PROVIDER = 'PROVIDER',
}

const getAuthFlowFromUser = (user: FetchUserAttributesOutput) => {
  if (user.identities) {
    return CognitoAuthFlowEnum.PROVIDER;
  }
  if (user['custom:password_type'] === 'PERMANENT') {
    return CognitoAuthFlowEnum.USER_PASSWORD;
  }
  return CognitoAuthFlowEnum.EMAIL_OTP;
};

export const useAuth = (initialLoading = false) => {
  const authenticateUser = useAuthStore((store) => store.authenticateUser);
  const logoutUser = useAuthStore((store) => store.logoutUser);
  const loadExpertProfile = useExpertProfileStore((store) => store.loadExpertProfile);
  const loadOnboardingData = useOnboardingStore((store) => store.loadOnboardingData);

  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

  const [startPasswordReset] = useStartPasswordResetMutation();
  const [verifyPasswordResetOtp] = useVerifyPasswordResetOtpMutation();
  const [createExpert] = useCreateExpertMutation();
  const [resendOtp] = useResendOtpMutation();
  const [getExpertHubState] = useExpertDataLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const [loadings, setLoadings] = useState({
    verifyAuth: initialLoading,
    submitEmail: false,
    submitResendOtp: false,
    submitPassword: false,
    submitOtp: false,
    signInWithGoogle: false,
    signInWithLinkedIn: false,
    changePassword: false,
    resetPassword: false,
    confirmPasswordReset: false,
    signOut: false,
  });

  const loadExpertRequiredData = useCallback(async (expertId: string, email: string) => {
    const expertData = await getExpertHubState().then(({ data }) => ({
      hubState: data?.expertHubState,
      profileData: data?.expertHubData,
    }));
    if (expertData) {
      const { profileData, hubState } = expertData;
      if (hubState) {
        loadOnboardingData(hubState);
      }
      if (profileData) {
        loadExpertProfile(profileData, expertId, email);
      }
    }
  }, []);

  const verifyAuthentication = useCallback(async (): Promise<FetchUserAttributesOutput | null> => {
    try {
      setLoadings((prev) => ({ ...prev, verifyAuth: true }));
      const user = await Auth.fetchUserAttributes();
      const expertId = await createExpert().then(({ data }) => data?.createExpertIdentity);
      if (user && expertId) {
        await loadExpertRequiredData(expertId, user.email || '');
        authenticateUser({
          email: user.email || '',
          expertId,
          cognitoId: user.sub || '',
          authFlow: getAuthFlowFromUser(user),
        });
      }
      return user;
    } catch (err) {
      if ((err as AuthError).name !== 'UserUnAuthenticatedException') {
        handleCognitoError(err);
      }
      return null;
    } finally {
      setLoadings((prev) => ({ ...prev, verifyAuth: false }));
    }
  }, []);

  const submitEmail = useCallback(async (email: string): Promise<CognitoAuthFlowEnum | null> => {
    try {
      setLoadings((prev) => ({ ...prev, submitEmail: true }));
      const { nextStep } = await Auth.signIn({
        username: prepareEmail(email),
        options: {
          authFlowType: 'CUSTOM_WITHOUT_SRP',
        },
      });
      setSubmittedEmail(email);
      return (nextStep as any).additionalInfo.flow as CognitoAuthFlowEnum;
    } catch (err) {
      if ((err as any).name === CognitoErrorCodesEnum.UserAlreadyAuthenticatedException) {
        await Auth.signOut();
      }
      handleCognitoError(err);
      return null;
    } finally {
      setLoadings((prev) => ({ ...prev, submitEmail: false }));
    }
  }, []);

  const refreshOtpSession = useCallback(async () => {
    if (!submittedEmail) {
      throw new Error('Cognito OTP session refresh failed. Missing submitted email!');
    }
    return submitEmail(submittedEmail);
  }, [submittedEmail]);

  const submitResendOtp = useCallback(
    async (email: string, isExternalOffer = false): Promise<'success' | 'failed'> => {
      try {
        setLoadings((prev) => ({ ...prev, submitResendOtp: true }));
        await resendOtp({
          variables: {
            email: prepareEmail(email),
            isExternalOffer,
          },
        });
        return 'success';
      } catch (err) {
        handleCognitoError(err);
        return 'failed';
      } finally {
        setLoadings((prev) => ({ ...prev, submitResendOtp: false }));
      }
    },
    [],
  );

  const submitPassword = useCallback(async (email: string, password: string) => {
    try {
      setLoadings((prev) => ({ ...prev, submitPassword: true }));
      const { isSignedIn } = await Auth.signIn({
        username: prepareEmail(email),
        password,
      });
      if (isSignedIn) {
        await createExpert();
        await verifyAuthentication();
      }
      return 'success';
    } catch (err) {
      handleCognitoError(err);
      return 'failed';
    } finally {
      setLoadings((prev) => ({ ...prev, submitPassword: false }));
    }
  }, []);

  const submitOtp = useCallback(
    async (otp: string, shouldVerifyAuthentication = true) => {
      try {
        setLoadings((prev) => ({ ...prev, submitOtp: true }));
        const res = await Auth.confirmSignIn({
          challengeResponse: otp,
        });

        if (!res.isSignedIn) {
          return 'failed';
        }
        if (shouldVerifyAuthentication) {
          await verifyAuthentication();
        }
        return 'success';
      } catch (err) {
        if (
          (err as { name: CognitoErrorCodesEnum }).name ===
          CognitoErrorCodesEnum.NotAuthorizedException
        ) {
          await refreshOtpSession();
          throw new Error(CognitoErrorCodesEnum.ExpiredCodeException);
        }
        handleCognitoError(err);
        return 'failed';
      } finally {
        setLoadings((prev) => ({ ...prev, submitOtp: false }));
      }
    },
    [refreshOtpSession],
  );

  const signInWithGoogle = useCallback(async () => {
    try {
      setLoadings((prev) => ({ ...prev, signInWithGoogle: true }));
      await Auth.signInWithRedirect({
        provider: 'Google',
      });
      return 'success';
    } catch (err) {
      handleCognitoError(err);
      return 'failed';
    } finally {
      setLoadings((prev) => ({ ...prev, signInWithGoogle: false }));
    }
  }, []);

  const signInWithLinkedIn = useCallback(async () => {
    try {
      setLoadings((prev) => ({ ...prev, signInWithLinkedIn: true }));
      await Auth.signInWithRedirect({
        provider: {
          custom: appConfig.cognito.linkedInCustomProviderName,
        },
      });
      return 'success';
    } catch (err) {
      handleCognitoError(err);
      return 'failed';
    } finally {
      setLoadings((prev) => ({ ...prev, signInWithLinkedIn: false }));
    }
  }, []);

  const changePassword = useCallback(async (oldPassword: string, newPassword: string) => {
    try {
      setLoadings((prev) => ({ ...prev, changePassword: true }));
      await Auth.updatePassword({ oldPassword, newPassword });
      return 'success';
    } catch (err) {
      handleCognitoError(err);
      return 'failed';
    } finally {
      setLoadings((prev) => ({ ...prev, changePassword: false }));
    }
  }, []);

  const resetPassword = useCallback(async (email: string): Promise<string> => {
    try {
      setLoadings((prev) => ({ ...prev, resetPassword: true }));
      const res = await startPasswordReset({
        variables: {
          email: prepareEmail(email),
        },
      });
      if (res.data?.startPasswordReset !== 'OK') {
        return 'false';
      }
      return 'success';
    } catch (err) {
      handleCognitoError(err);
      return 'failed';
    } finally {
      setLoadings((prev) => ({ ...prev, resetPassword: false }));
    }
  }, []);

  const confirmPasswordReset = useCallback(async (email: string, code: string) => {
    try {
      setLoadings((prev) => ({ ...prev, confirmPasswordReset: true }));
      const result = await verifyPasswordResetOtp({
        variables: {
          email: prepareEmail(email),
          otp: code,
        },
      });
      if (!result.data?.verifyPasswordResetOtp) {
        return 'failed';
      }
      await submitEmail(email);
      await submitOtp(code);
      return 'success';
    } catch (err) {
      handleCognitoError(err);
      return 'failed';
    } finally {
      setLoadings((prev) => ({ ...prev, confirmPasswordReset: false }));
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      setLoadings((prev) => ({ ...prev, signOut: true }));
      await Auth.signOut();
      logoutUser();
      return 'success';
    } catch (err) {
      handleCognitoError(err);
      return 'failed';
    } finally {
      setLoadings((prev) => ({ ...prev, signOut: true }));
    }
  }, []);

  return {
    verifyAuthentication,
    submitEmail,
    submitPassword,
    submitOtp,
    submitResendOtp,
    signInWithGoogle,
    signInWithLinkedIn,
    resetPassword,
    confirmPasswordReset,
    changePassword,
    signOut,
    loadings: {
      verifyAuth: loadings.verifyAuth,
      submitEmail: loadings.submitEmail,
      submitResendOtp: loadings.submitResendOtp,
      submitPassword: loadings.submitPassword,
      submitOtp: loadings.submitOtp,
      signInWithGoogle: loadings.signInWithGoogle,
      signInWithLinkedIn: loadings.signInWithLinkedIn,
      changePassword: loadings.changePassword,
      resetPassword: loadings.resetPassword,
      confirmPasswordReset: loadings.confirmPasswordReset,
      signOut: loadings.signOut,
    },
  };
};
